import React, { useEffect, useState } from "react";
import { server } from "../../server";
import FeeReceipt from "../../components/FeeReceipt";
import axios from "axios";
import TuitionFeeCertificatePage from "../../components/TuitionFeeCertificate";

const FeeLedger = ({ feeDetails, button = true }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [studentData, setStudentData] = useState(null);

  useEffect(() => {
    const loadStudent = async () => {
      if (!feeDetails?.admission_no) return;
      const st = await fetchStudent(feeDetails.admission_no);
      setStudentData(st);
    };
    loadStudent();
  }, [feeDetails?.admission_no]);

  if (!feeDetails) return null;

  const payments = {};

  const fetchStudent = async (admissionNo) => {
    if (!admissionNo) {
      setError("Please enter Admission No");
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${server}/getParticularStudentMongodb`,
        { admission_no: admissionNo },
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );
      const studentData = response.data?.["body-json"]?.body?.student;
      if (studentData) {
        return studentData;
      } else {
        setError("No Student Data Found");
        return null;
      }
    } catch (err) {
      setError("Student not found or API error.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchFees = async (receiptNo) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${server}/feeHistory_using_receipt_no_mongodb`,
        { receipt_no: receiptNo },
        {
          headers: { Authorization: `${localStorage.getItem("token")}` },
        },
      );

      const responseData = response.data?.["body-json"]?.body?.data;
      return responseData;
    } catch (error) {
      console.error("Error fetching fee details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async (feeData, receiptNo) => {
    try {
      const student =
        studentData || (await fetchStudent(feeDetails.admission_no));

      const receiptDetailsRaw = await fetchFees(receiptNo);
      const receiptRecord = Array.isArray(receiptDetailsRaw)
        ? receiptDetailsRaw[0]
        : receiptDetailsRaw;

      if (!receiptRecord) {
        console.error("No receipt record found for receipt_no:", receiptNo);
        return;
      }

      if (student) FeeReceipt([{ ...receiptRecord, student }], student);
    } catch (e) {
      console.error(e);
    }
  };

  const addPayment = (receiptNo, item) => {
    if (!receiptNo || receiptNo.trim() === "") return;

    if (!payments[receiptNo]) {
      payments[receiptNo] = {
        receiptNo,
        date: item.date || "N/A",
        paymentMode: Array.isArray(item.paymentMode)
          ? item.paymentMode.join(", ")
          : item.paymentMode || "N/A",
        months: [],
        items: {},
        totalAmount: 0,
        collectedBy: item.collectedBy || "",
      };
    }

    if (!payments[receiptNo].collectedBy && item.collectedBy) {
      payments[receiptNo].collectedBy = item.collectedBy;
    }

    if (!payments[receiptNo].items[item.category]) {
      payments[receiptNo].items[item.category] = {
        category: item.category,
        amt: 0,
      };
    }

    payments[receiptNo].items[item.category].amt += item.amt || 0;
    payments[receiptNo].totalAmount += item.amt || 0;
  };

  if (feeDetails.developmentCharges?.receiptNo?.trim()) {
    addPayment(feeDetails.developmentCharges.receiptNo, {
      category: "Development Charges",
      amt: feeDetails.developmentCharges.amt || 0,
      date: feeDetails.developmentCharges.date || "N/A",
      paymentMode: feeDetails.developmentCharges.paymentMode || "N/A",
      collectedBy: feeDetails.developmentCharges.collectedBy || "",
    });
  }

  if (feeDetails.admissionFee?.receiptNo?.trim()) {
    addPayment(feeDetails.admissionFee.receiptNo, {
      category: "Admission Fee",
      amt: feeDetails.admissionFee.amt || 0,
      date: feeDetails.admissionFee.date || "N/A",
      paymentMode: feeDetails.admissionFee.paymentMode || "N/A",
      collectedBy: feeDetails.admissionFee.collectedBy || "",
    });
  }

  if (Array.isArray(feeDetails.monthPaid)) {
    feeDetails.monthPaid.forEach((month) => {
      const { monthlyFee, transportFee, labFee } = month;

      if (monthlyFee?.paid && monthlyFee?.receiptNo?.trim()) {
        addPayment(monthlyFee.receiptNo, {
          category: "Monthly Fee",
          amt: monthlyFee.amount ?? 0,
          date: monthlyFee.date || "N/A",
          paymentMode:
            Object.keys(monthlyFee.paymentMode || {}).length > 0
              ? Object.entries(monthlyFee.paymentMode)
                  .map(([mode, details]) =>
                    typeof details === "object"
                      ? `${mode}: ${details.amount}`
                      : mode,
                  )
                  .join(", ")
              : "N/A",
          collectedBy: monthlyFee.collectedBy || "",
        });

        payments[monthlyFee.receiptNo].months.push(month.month || "Unknown");
      }

      if (transportFee?.paid && transportFee?.receiptNo?.trim()) {
        addPayment(transportFee.receiptNo, {
          category: "Transport Fee",
          amt: transportFee.amount ?? 0,
          date: transportFee.date || "N/A",
          paymentMode:
            Object.keys(transportFee.paymentMode || {}).length > 0
              ? Object.entries(transportFee.paymentMode)
                  .map(([mode, details]) =>
                    typeof details === "object"
                      ? `${mode}: ${details.amount}`
                      : mode,
                  )
                  .join(", ")
              : "N/A",
          collectedBy: transportFee.collectedBy || "",
        });

        if (!payments[transportFee.receiptNo].months.includes(month.month)) {
          payments[transportFee.receiptNo].months.push(
            month.month || "Unknown",
          );
        }
      }

      if (labFee?.paid && labFee?.receiptNo?.trim()) {
        addPayment(labFee.receiptNo, {
          category: "Lab Fee",
          amt: labFee.amount ?? 0,
          date: labFee.date || "N/A",
          paymentMode:
            Object.keys(labFee.paymentMode || {}).length > 0
              ? Object.entries(labFee.paymentMode)
                  .map(([mode, details]) =>
                    typeof details === "object"
                      ? `${mode}: ${details.amount}`
                      : mode,
                  )
                  .join(", ")
              : "N/A",
          collectedBy: labFee.collectedBy || "",
        });

        if (!payments[labFee.receiptNo].months.includes(month.month)) {
          payments[labFee.receiptNo].months.push(month.month || "Unknown");
        }
      }
    });
  }

  return (
    <div className="col-12 mt-3 z-0">
      <ul className="list-group list-group-flush list-timeline-activity z-0">
        {Object.values(payments).map((payment, index) => (
          <li className="list-group-item px-0 pt-0 border-0 mb-3" key={index}>
            <div className="row">
              <div className="col-auto">
                <div className="icon-shape icon-md rounded-circle bg-primary text-white position-relative z-1">
                  <i className="bi bi-wallet2"></i>
                </div>
              </div>
              <div className="col ms-n2 mt-1">
                <div className="d-flex flex-column">
                  <h4 className="mb-0 lh-sm">
                    {payment.receiptNo}
                    {payment.receiptNo?.startsWith("E00") && (
                      <span className="badge bg-danger-soft ms-2">
                        Previous ERP
                      </span>
                    )}
                  </h4>
                  <span className="fs-6">
                    {payment.date.split("-").join("/")}
                    {payment.collectedBy && (
                      <span className="ms-2 text-muted">
                        •
                        <span className="ms-2 text-muted">
                          Collected By &mdash; {payment.collectedBy}
                        </span>
                      </span>
                    )}
                  </span>
                  {payment.months.length > 0 && (
                    <h5 className="my-2 lh-sm">{payment.months.join(", ")}</h5>
                  )}
                  <span className="fs-5 lh-sm">
                    {Object.values(payment.items).map((item, i) => (
                      <React.Fragment key={i}>
                        {item.category} - {item.amt}/-
                        <br />
                      </React.Fragment>
                    ))}
                  </span>
                </div>
                <h5 className="mb-0 mt-3 text-success">
                  {payment.totalAmount}/-
                </h5>
                {button && !payment.receiptNo?.startsWith("E00") && (
                  <span
                    onClick={() =>
                      handleDownloadReceipt(feeDetails, payment.receiptNo)
                    }
                    className="mt-2 cursor-pointer rounded-4 btn btn-tag btn-sm px-4"
                  >
                    Download Receipt
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      <div className="pb-3">
        <TuitionFeeCertificatePage
          feeDetails={feeDetails}
          student={studentData}
        />
      </div>
    </div>
  );
};

export default FeeLedger;
